import { yupResolver } from '@hookform/resolvers/yup';
import { hasFeature } from '@precert/has-feature';
import { FormSchema } from '@precert/validation';
import axios from 'axios';
import type { SetStateAction, Dispatch } from 'react';
import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Spinner, FormGroup, Row, Col } from 'reactstrap';
import urlJoin from 'url-join';
import * as Yup from 'yup';

import { PageLoader } from '../../../_components';
import { Button } from '../../../_components/button';
import { useConfig } from '../../../_components/config';
import { DateFieldReferralDate } from '../../../_components/date-field/date-referral-check';
import SingleErrorDisplay from '../../../_components/error-display/single-error-display';
import { useToastr } from '../../../_components/toastr';
import { useCheckSession } from '../../../_components/use-check-session';
import type { AttachmentFormData } from '../create/steps/step-clinical-documents';
import { StepAttachments } from '../create/steps/step-clinical-documents';
import { PrecertCodeField } from '../create/steps/step-new-request';

interface UpdatePrecertificationParams {
  dateOfService?: Date;
  attachments?: AttachmentFormData[];
  procedureCode?: string;
  procedureCodesList?: string[];
}
Yup.addMethod(Yup.object, 'atLeastOneOf', function (list) {
  return this.test({
    name: 'atLeastOneOf',
    message:
      'This form must have at least one of these fields updated: Date Of Service, Procedure Code, Attachments',
    exclusive: true,
    params: { keys: list.join(', ') },
    test: (value) => value === null || list.some((f: string) => !!value[f]),
  });
});

export function UpdateForm({
  precertificationId,
  setSubmitted,
  submitted,
  referralDate,
  referralExpirationDate,
  procedureCode,
  procedureCodesList,
}: {
  precertificationId: string;
  setSubmitted: Dispatch<SetStateAction<boolean>>;
  submitted: boolean;
  referralDate: string;
  referralExpirationDate: string;
  procedureCode?: string;
  procedureCodesList?: string[];
}) {
  const { loading } = useCheckSession();
  const navigate = useNavigate();
  const { api, features } = useConfig();

  const baseSchema = FormSchema().pick(['procedureCode', 'procedureCodesList']);

  const schema = Yup.object()
    .shape({
      attachments: Yup.array()
        .optional()
        .of(
          Yup.object().shape({
            uploaded: Yup.boolean(),
            fileName: Yup.string().required('File name is required'),
            contentType: Yup.string().required('Content Type is required'),
            contentLength: Yup.number().required('Content Length is required'),
            attachmentId: Yup.string().required('Attachment ID is required'),
          })
        ),
      dateOfService: Yup.date().optional(),
      ...(hasFeature('procedure-codes-list', features)
        ? { procedureCodesList: baseSchema.fields.procedureCodesList }
        : { procedureCode: baseSchema.fields.procedureCode }),
    })
    .atLeastOneOf([
      'attachments',
      'dateOfService',
      'procedureCode',
      'procedureCodesList',
    ]);
  const toastr = useToastr();
  const { mutateAsync: submit, isLoading: submitLoading } = useMutation({
    mutationFn: async (d: UpdatePrecertificationParams) =>
      axios({
        url: urlJoin(api, 'update'),
        method: 'post',
        data: {
          formData: {
            attachments: d.attachments,
            dateOfService: d.dateOfService,
            ...(hasFeature('procedure-codes-list', features)
              ? { procedureCodesList: d.procedureCodesList }
              : { procedureCode: d.procedureCode }),
          },
          precertificationId,
          // track the user's browser time & time zone, for auditing/troubleshooting
          browserTime: new Date(),
          browserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        withCredentials: true,
      }),
    onSuccess: (response) => {
      const { data } = response;
      const queryParams = new URLSearchParams();
      if (data?.error) {
        queryParams.set('error', data.error);
      }
      if (data?.caution) {
        queryParams.set('caution', data.caution);
      }
      if (data?.processingId) {
        queryParams.set('processingId', data.processingId);
      }
      const url =
        urlJoin('/submitted', precertificationId) +
        (queryParams.toString() ? `?${queryParams.toString()}` : '');
      navigate(url);
      setSubmitted(true);
    },
    onError: (err: string) => {
      toastr.error(<span>Failed to submit</span>, {
        details: err,
      });
    },
  });

  const defaultValues: Partial<UpdatePrecertificationParams> = {};
  if (hasFeature('procedure-codes-list', features)) {
    defaultValues.procedureCodesList = procedureCodesList?.length
      ? procedureCodesList
      : procedureCode
        ? [procedureCode]
        : [];
  } else {
    defaultValues.procedureCode = procedureCode;
  }

  const form = useForm({
    mode: 'all',
    resolver: yupResolver(schema),
    context: { features },
    defaultValues,
  });
  const { handleSubmit, formState } = form;
  const keys = Object.keys(schema.fields);
  const isDisabled = Object.keys(formState?.errors || {}).some((f) =>
    keys.includes(f)
  );
  const requestUpdate = async (args: UpdatePrecertificationParams) => {
    // Only send fields that were actually changed (dirty)
    const changedData: UpdatePrecertificationParams = {};

    if (formState.dirtyFields.dateOfService) {
      changedData.dateOfService = args.dateOfService;
    }

    // Attachments are checked for existence (new uploads) rather than dirty state
    if (args.attachments && args.attachments.length > 0) {
      changedData.attachments = args.attachments;
    }

    if (hasFeature('procedure-codes-list', features)) {
      if (formState.dirtyFields.procedureCodesList) {
        changedData.procedureCodesList = args.procedureCodesList;
      }
    } else {
      if (formState.dirtyFields.procedureCode) {
        changedData.procedureCode = args.procedureCode;
      }
    }

    // Prevent submitting when no fields are actually being updated
    if (Object.keys(changedData).length === 0) {
      toastr.error(<span>No changes detected</span>, {
        details: 'Please modify at least one field before submitting',
      });
      return;
    }

    const result = await submit(changedData);
    return result;
  };
  if (loading) return <PageLoader />;
  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(requestUpdate)} noValidate>
        <hr className="my-4" />
        <h2 tabIndex={0} className="fw-bold h6 mb-3">
          Update Precertification Details
        </h2>
        <Row className="align-items-start">
          <Col lg={6}>
            <FormGroup>
              <DateFieldReferralDate
                name="dateOfService"
                label="Date Of Service"
                required={false}
                isUpdate={true}
                referralDate={referralDate}
                referralExpirationDate={referralExpirationDate}
              />
            </FormGroup>
          </Col>
          <Col lg={6}>
            <FormGroup>
              <PrecertCodeField
                customSubLabel={
                  <p className="text-muted small mb-3">
                    <strong>Please Note:</strong> The procedure codes you enter
                    here will replace the procedure codes entered in the
                    original request.
                  </p>
                }
              />
            </FormGroup>
          </Col>
        </Row>
        <div className="mb-3 text-muted small">
          <em>
            CPT copyright 2024 American Medical Association. All rights
            reserved.
          </em>
        </div>
        <h2 tabIndex={0} className="fw-bold h6 mb-1">
          Attachments
        </h2>
        <p className="text-muted mb-3">
          Additional documentation to support justification for the service
          being performed.
        </p>
        <StepAttachments isUpdate={true} />
        <Button
          color="success"
          className="mb-3"
          aria-description="Press Enter to update this precertification"
          type="submit"
          id="identify-e5b4d7cb-f8be-43a5-b273-a6736c3dcb8f"
          disabled={isDisabled || submitted || submitLoading}
        >
          Submit
          {submitLoading && <Spinner size="sm" className="ms-2" />}
        </Button>
        <SingleErrorDisplay field="" className="mb-3" />
      </form>
    </FormProvider>
  );
}
